import { useEffect, useState } from 'react';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents, MESES } from '@/features/catalog/catalog';
import { colorDe } from '@/ui/ColorParticipante';
import { listAllParticipants, setWorksStatus, type Participant } from '@/features/works/works';
import { SelectorPeriodo } from '@/features/period/SelectorPeriodo';
import { ConfirmarAccion } from '@/ui/ConfirmarAccion';
import { AvisoFlotante } from '@/ui/AvisoFlotante';
import { Boton, EncabezadoPantalla, Error, Importe, SinNada } from '@/ui/Sistema';
import { fetchRepartoDelMes } from './reparto';
import { clientesDelMes, faltanFacturar, type ClienteDelMes } from './porCliente';

/**
 * Para facturar: el mes agrupado por cliente.
 *
 * ## Por qué es una pantalla y no una vista más del resumen
 *
 * El resumen del mes va por fecha, porque así se carga y así se revisa contra
 * la planilla. Facturar va por cliente. Son dos recorridos distintos de los
 * mismos datos y ninguno sirve para lo del otro: con el resumen abierto, armar
 * la factura de un cliente con cuatro trabajos era ir juntándolos a ojo entre
 * los otros dieciocho, anotando cantidades en un papel.
 *
 * Y va a Inicio, no abajo del resumen. El reparto trabajo por trabajo ya pasó
 * por eso: estuvo detrás de un enlace al pie y ahí no lo encontraba nadie. Lo
 * que se usa todos los días de facturación tiene que estar en el tablero.
 *
 * ## Las cantidades son el punto
 *
 * Cada cliente muestra sus honorarios SUMADOS —ciento ochenta tactos, doscientos
 * diez kilómetros— que es exactamente lo que se escribe en la factura. Antes
 * ese número no existía en ninguna pantalla: había que abrir los cuatro
 * trabajos y sumar.
 *
 * ## Facturar de a cliente, no de a trabajo
 *
 * La factura es una sola para los cuatro, así que marcarlos uno por uno es
 * cuatro gestos para un solo hecho. El botón los marca juntos. Lo mismo con el
 * cobro: la plata llega de un cliente, no de un trabajo.
 *
 * No congela nada que no congelara antes: facturar es la misma `set_works_status`
 * que ya usa el listado, con los mismos permisos y los mismos candados. Y se
 * puede desfacturar.
 *
 * ## El nombre del cliente va pintado con el color de quien factura
 *
 * Lo mismo que en el resumen del mes, y por la misma razón: facturar es ir
 * cliente por cliente preguntándose «¿este lo facturo yo o lo factura Felipe?».
 * El color contesta eso antes de leer. Pero el nombre de quien factura está
 * escrito igual, debajo: el color es un atajo para la vista, nunca el único
 * portador del dato —hay quien no eligió colores y hay quien no distingue dos—.
 *
 * Un cliente al que le facturan VARIOS no se pinta. Pasa de verdad: a un mismo
 * cliente se le factura una cosa por uno y otra por otro. Pintarlo con el color
 * del primero sería inventar un dato justo en el renglón más grande de la
 * tarjeta; entonces se dice que son varios y cada trabajo muestra el suyo.
 *
 * ## El orden es alfabético, y se queda quieto
 *
 * Tentaba poner primero los que faltan. Pero entonces facturar un cliente lo
 * saca de arriba y la lista salta bajo el dedo justo después de tocarla, con el
 * siguiente cliente ocupando el lugar donde estaba el botón que se acaba de
 * apretar. Cuántos faltan se dice con un número arriba; dónde está cada uno no
 * se mueve.
 */
interface Props {
  onVolver: () => void;
  onVerTrabajo: (workId: string) => void;
}

export function ParaFacturarPage({ onVolver, onVerTrabajo }: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  const { year, month } = usePeriod();
  const sinConexion = useSinConexion();

  const [clientes, setClientes] = useState<ClienteDelMes[] | null>(null);
  const [gente, setGente] = useState<Participant[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Qué se está por confirmar: el cliente y si es facturar o cobrar. */
  const [porConfirmar, setPorConfirmar] = useState<{
    cliente: ClienteDelMes;
    accion: 'facturar' | 'cobrar';
  } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /**
   * Para volver a pedir el mes después de marcar algo.
   *
   * Lo que se ve tiene que ser lo que hay: marcar cuatro trabajos como
   * facturados y dejar la pantalla mostrando «Facturar 4» es ofrecer otra vez
   * lo que ya se hizo.
   */
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      setCargando(true);
      try {
        const d = await fetchRepartoDelMes(orgId, year, month);
        if (cancelado) return;
        setClientes(clientesDelMes(d));
        setError(null);
      } catch (e) {
        if (cancelado) return;
        setError(mensajeDeError(e));
        setClientes(null);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month, recarga]);

  // Aparte y en silencio: si falla, la pantalla se ve igual, sin colores. El
  // nombre de quien factura está escrito al lado, así que no se pierde nada
  // más que el atajo.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const l = await listAllParticipants(orgId);
        if (!cancelado) setGente(l);
      } catch {
        // Sin colores se lee igual.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  /** El estilo para pintar algo con el color de alguien, o nada si no eligió. */
  const pintar = (pid: string | null) => {
    const c = pid ? colorDe(gente.find((g) => g.id === pid)?.color) : null;
    return c ? { color: c } : undefined;
  };

  /** Su nombre. La cuenta técnica se dice como lo que es. */
  const nombreDeQuien = (pid: string | null) => {
    const p = gente.find((g) => g.id === pid);
    if (!p) return null;
    return p.kind === 'org_account' ? `${p.display_name} (la veterinaria)` : p.display_name;
  };

  const confirmar = async () => {
    if (!porConfirmar) return;
    const { cliente, accion } = porConfirmar;
    const ids = accion === 'facturar' ? cliente.porFacturar : cliente.porCobrar;
    setOcupado(true);
    try {
      const r = await setWorksStatus(
        ids,
        accion === 'facturar' ? { isBilled: true } : { isPaid: true },
      );
      setPorConfirmar(null);
      // Lo que se dice es lo que PASÓ, no lo que se pidió. Un trabajo cerrado o
      // bloqueado no cambia, y anunciar que sí lo haría invisible.
      setAviso(
        r.modificados === ids.length
          ? `${cliente.client_name}: ${r.modificados} ${accion === 'facturar' ? 'facturado' : 'cobrado'}${r.modificados === 1 ? '' : 's'}.`
          : `Se cambiaron ${r.modificados} de ${ids.length}. Los demás no se pudieron tocar.`,
      );
      setRecarga((r) => r + 1);
    } catch (e) {
      setError(mensajeDeError(e));
      setPorConfirmar(null);
    } finally {
      setOcupado(false);
    }
  };

  if (!activeOrg || !orgId) return null;

  const faltan = clientes ? faltanFacturar(clientes) : 0;

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="para-facturar">
      <EncabezadoPantalla
        titulo="Para facturar"
        nota="El mes por cliente, con las cantidades sumadas"
        onVolver={onVolver}
      />

      <SelectorPeriodo testId="facturar-periodo" />

      <Error testId="facturar-error">{error}</Error>

      {cargando && !clientes && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

      {clientes && clientes.length === 0 && (
        <SinNada texto={`No hay trabajos en ${MESES[month - 1]}.`} testId="facturar-vacio" />
      )}

      {clientes && clientes.length > 0 && (
        <p className="text-sm text-[var(--tinta-suave)]" data-testid="facturar-cuantos">
          {clientes.length} cliente{clientes.length === 1 ? '' : 's'}
          {faltan > 0 ? (
            <>
              {' · '}
              <span className="font-medium text-[var(--aviso)]">
                {faltan} trabajo{faltan === 1 ? '' : 's'} sin facturar
              </span>
            </>
          ) : (
            <span className="text-[var(--exito)]">{' · '}todo facturado</span>
          )}
        </p>
      )}

      <ul className="flex flex-col gap-3" data-testid="facturar-lista">
        {clientes?.map((c) => (
          <li
            key={c.client_name}
            className="flex flex-col gap-2 superficie p-4"
            data-testid="facturar-cliente"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span
                className="min-w-0 truncate font-semibold leading-tight"
                style={pintar(c.facturadoPor)}
                data-testid="facturar-cliente-nombre"
              >
                {c.client_name}
              </span>
              <Importe>{formatCents(c.total_cents)}</Importe>
            </div>

            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-[var(--tinta-tenue)]">
              <span>
                {c.trabajos.length} trabajo{c.trabajos.length === 1 ? '' : 's'}
                {/* Un total al que le faltan trabajos no se presenta como si
                    estuviera completo: `NULL` es PENDIENTE, nunca 0. */}
                {c.sinPrecio > 0 && (
                  <span className="text-[var(--aviso)]">
                    {' · '}
                    {c.sinPrecio} sin precio, el total está incompleto
                  </span>
                )}
              </span>

              {/* Quién factura, escrito. El color de arriba es el atajo; esto
                  es el dato, y es lo que se lee cuando no hay colores. */}
              {c.facturanVarios ? (
                <span className="text-[var(--aviso)]" data-testid="facturar-quien">
                  · Lo facturan varios
                </span>
              ) : (
                <span data-testid="facturar-quien">
                  {'· Factura '}
                  <span className="font-medium" style={pintar(c.facturadoPor)}>
                    {nombreDeQuien(c.facturadoPor) ?? 'sin asignar'}
                  </span>
                </span>
              )}
            </span>

            {/* --- Los renglones de la factura --- */}
            {c.cantidades.length > 0 && (
              <div
                className="flex flex-col gap-0.5 border-t border-[var(--linea)] pt-2 text-sm"
                data-testid="facturar-cantidades"
              >
                {c.cantidades.map((q, i) => (
                  <span
                    key={`${q.nombre}-${i}`}
                    className="flex items-baseline justify-between gap-3"
                    data-testid="facturar-cantidad"
                  >
                    <span className="min-w-0 text-[var(--tinta-suave)]">{q.nombre}</span>
                    {q.cantidad !== null ? (
                      <span className="shrink-0 font-semibold tabular-nums">{q.cantidad}</span>
                    ) : (
                      q.dudosa && (
                        <span className="shrink-0 text-xs text-[var(--aviso)]">
                          cantidad sin confirmar
                        </span>
                      )
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* --- De qué trabajos sale ---------------------------------------
                Van abajo y chicos: lo que se factura es lo de arriba. Pero
                tienen que estar, porque cuando un número no cuadra lo primero
                que se pregunta es de qué visita salió.
               -------------------------------------------------------------- */}
            <ul className="flex flex-col divide-y divide-[var(--linea)] border-t border-[var(--linea)] pt-1">
              {c.trabajos.map((t) => (
                <li key={t.work_session_id}>
                  <button
                    type="button"
                    onClick={() => onVerTrabajo(t.work_session_id)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 py-1 text-left text-xs"
                    data-testid="facturar-trabajo"
                  >
                    <span className="min-w-0">
                      <span className="text-[var(--tinta-suave)]">
                        {new Date(`${t.work_date}T00:00:00`).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </span>
                      {t.establishment_name && (
                        <span className="ml-2 text-[var(--tinta-tenue)]">
                          {t.establishment_name}
                        </span>
                      )}
                      {/* Sólo cuando no es el mismo para todos: repetir
                          «Manuel» cuatro veces abajo de «Factura Manuel» no
                          agrega nada y tapa la fecha y el campo. */}
                      {c.facturanVarios && (
                        <span
                          className="ml-2 font-medium"
                          style={pintar(t.billed_to_participant_id)}
                          data-testid="facturar-trabajo-quien"
                        >
                          {nombreDeQuien(t.billed_to_participant_id) ?? 'sin asignar'}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-2">
                      <span className="tabular-nums">{formatCents(t.total_cents)}</span>
                      <span
                        className={
                          t.is_paid
                            ? 'text-[var(--exito)]'
                            : t.is_billed
                              ? 'text-[var(--accion)]'
                              : 'text-[var(--tinta-tenue)]'
                        }
                      >
                        {t.is_paid ? '✓ cobrado' : t.is_billed ? '✓ facturado' : '○'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* --- Y marcarlos juntos ---------------------------------------
                La factura es una sola para los cuatro trabajos. Marcarlos de a
                uno es cuatro gestos para un solo hecho.

                Sin señal no se ofrece: esto escribe en el servidor, y un botón
                que parece hacer algo y no lo hace es peor que no tenerlo.
               -------------------------------------------------------------- */}
            {sinConexion ? (
              (c.porFacturar.length > 0 || c.porCobrar.length > 0) && (
                <p className="text-xs text-[var(--tinta-tenue)]" data-testid="facturar-sin-senal">
                  Sin señal no se puede marcar. Lo que cargues se guarda igual.
                </p>
              )
            ) : (
              <div className="flex flex-wrap gap-2">
                {c.porFacturar.length > 0 && (
                  <Boton
                    jerarquia="primario"
                    onClick={() => setPorConfirmar({ cliente: c, accion: 'facturar' })}
                    data-testid="facturar-boton"
                  >
                    Facturar {c.porFacturar.length}
                  </Boton>
                )}
                {c.porCobrar.length > 0 && (
                  <Boton
                    onClick={() => setPorConfirmar({ cliente: c, accion: 'cobrar' })}
                    data-testid="cobrar-boton"
                  >
                    Marcar {c.porCobrar.length} cobrado{c.porCobrar.length === 1 ? '' : 's'}
                  </Boton>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {porConfirmar && (
        <ConfirmarAccion
          titulo={porConfirmar.accion === 'facturar' ? 'Facturar' : 'Marcar cobrado'}
          pregunta={
            porConfirmar.accion === 'facturar'
              ? `¿Marcás como facturados los ${porConfirmar.cliente.porFacturar.length} trabajos de ${porConfirmar.cliente.client_name}?`
              : `¿Marcás como cobrados los ${porConfirmar.cliente.porCobrar.length} trabajos de ${porConfirmar.cliente.client_name}?`
          }
          {...(porConfirmar.accion === 'facturar'
            ? { detalle: 'Un trabajo facturado deja de poder corregirse hasta que se desfacture.' }
            : {})}
          confirmar={porConfirmar.accion === 'facturar' ? 'Facturar' : 'Marcar cobrado'}
          ocupado={ocupado}
          onConfirmar={() => void confirmar()}
          onCancelar={() => setPorConfirmar(null)}
          testId="facturar-confirmar"
        />
      )}

      <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />
    </div>
  );
}
